// ============================================
// 背景・Three.js状態管理
// ============================================

import type * as THREE from 'three';
import type { BgStar, BgBrightStar, BgAsteroid, BgNebulaLayer } from '../types/game-types';

// --- ステージ背景関連変数 ---
let _currentStageIndex: number = 0;
let _stageTransitionTimer: number = 0;
let _stageNameDisplayTimer: number = 0;
let _isStageTransitioning: boolean = false;
let _prevStageIndex: number = 0;
let _bgStars: BgStar[] = [];
let _bgBrightStars: BgBrightStar[] = [];
let _bgAsteroids: BgAsteroid[] = [];
let _bgNebulaLayers: BgNebulaLayer[] = [];
let _bgNebulaOffset: number = 0;
let _distantGiantY: number = 0;
let _bgPlanetY: number = 760;
let _subspaceWarpOffset: number = 0;
let _stageParticleAlpha: number = 1;
let _lowOrbitPlanetMask: { x: number; y: number; r: number; r2: number } | null = null;

// --- Three.js関連変数 ---
let _threeScene: THREE.Scene | null = null;
let _threeCamera: THREE.PerspectiveCamera | null = null;
let _threeRenderer: THREE.WebGLRenderer | null = null;
let _threeBgMesh: THREE.Mesh | null = null;
let _threeBgTexture: THREE.Texture | null = null;
let _lastTopHex: string = '';
let _lastBottomHex: string = '';
let _lastBgName: string = '';
let _lastBgProgress: number = -1;
let _earthGroup: THREE.Group | null = null;
let _earthMesh: THREE.Mesh | null = null;
let _cloudMesh: THREE.Mesh | null = null;
let _atmosphereMesh: THREE.Mesh | null = null;
let _threeNebulaGroup: THREE.Group | null = null;
let _threeNebulaMeshes: THREE.Mesh[] = [];
let _threeInitialized: boolean = false;

// Deep Spaceパターン（Galaxy Cluster固定）
let _deepSpacePatterns: { x: number; y: number; size: number; rotation: number; opacity: number; color: THREE.Color }[] = [];

// --- Moon state ---
let _moonGroup: THREE.Group | null = null;
let _moonMesh: THREE.Mesh | null = null;
let _moonY: number = 200;

// --- Space Station state ---
let _spaceStationGroup: THREE.Group | null = null;
let _spaceStationY: number = 100;
let _stationLightTimer: number = 0;

// --- Mars state ---
let _marsGroup: THREE.Group | null = null;
let _marsMesh: THREE.Mesh | null = null;
let _marsY: number = 600;
let _marsDustParticles: { x: number; y: number; vx: number; vy: number; size: number; alpha: number }[] = [];
let _phobosMesh: THREE.Mesh | null = null;
let _deimosMesh: THREE.Mesh | null = null;

// --- Jupiter state ---
let _jupiterGroup: THREE.Group | null = null;
let _jupiterMesh: THREE.Mesh | null = null;
let _jupiterY: number = 300;

// ============================================
// Getters and Setters
// ============================================

// --- ステージ背景関連 ---
export const getCurrentStageIndex = (): number => _currentStageIndex;
export const setCurrentStageIndex = (val: number): void => {
    _currentStageIndex = val;
};

export const getStageTransitionTimer = (): number => _stageTransitionTimer;
export const setStageTransitionTimer = (val: number): void => {
    _stageTransitionTimer = val;
};

export const getStageNameDisplayTimer = (): number => _stageNameDisplayTimer;
export const setStageNameDisplayTimer = (val: number): void => {
    _stageNameDisplayTimer = val;
};

export const getIsStageTransitioning = (): boolean => _isStageTransitioning;
export const setIsStageTransitioning = (val: boolean): void => {
    _isStageTransitioning = val;
};

export const getPrevStageIndex = (): number => _prevStageIndex;
export const setPrevStageIndex = (val: number): void => {
    _prevStageIndex = val;
};

export const getBgStars = (): BgStar[] => _bgStars;
export const setBgStars = (val: BgStar[]): void => {
    _bgStars = val;
};

export const getBgBrightStars = (): BgBrightStar[] => _bgBrightStars;
export const setBgBrightStars = (val: BgBrightStar[]): void => {
    _bgBrightStars = val;
};

export const getBgAsteroids = (): BgAsteroid[] => _bgAsteroids;
export const setBgAsteroids = (val: BgAsteroid[]): void => {
    _bgAsteroids = val;
};

export const getBgNebulaLayers = (): BgNebulaLayer[] => _bgNebulaLayers;
export const setBgNebulaLayers = (val: BgNebulaLayer[]): void => {
    _bgNebulaLayers = val;
};

export const getBgNebulaOffset = (): number => _bgNebulaOffset;
export const setBgNebulaOffset = (val: number): void => {
    _bgNebulaOffset = val;
};

export const getDistantGiantY = (): number => _distantGiantY;
export const setDistantGiantY = (val: number): void => {
    _distantGiantY = val;
};

export const getBgPlanetY = (): number => _bgPlanetY;
export const setBgPlanetY = (val: number): void => {
    _bgPlanetY = val;
};

export const getSubspaceWarpOffset = (): number => _subspaceWarpOffset;
export const setSubspaceWarpOffset = (val: number): void => {
    _subspaceWarpOffset = val;
};

export const getStageParticleAlpha = (): number => _stageParticleAlpha;
export const setStageParticleAlpha = (val: number): void => {
    _stageParticleAlpha = val;
};

export const getLowOrbitPlanetMask = (): { x: number; y: number; r: number; r2: number } | null => _lowOrbitPlanetMask;
export const setLowOrbitPlanetMask = (val: { x: number; y: number; r: number; r2: number } | null): void => {
    _lowOrbitPlanetMask = val;
};

// --- Three.js関連 ---
export const getThreeScene = (): THREE.Scene | null => _threeScene;
export const setThreeScene = (val: THREE.Scene | null): void => {
    _threeScene = val;
};

export const getThreeCamera = (): THREE.PerspectiveCamera | null => _threeCamera;
export const setThreeCamera = (val: THREE.PerspectiveCamera | null): void => {
    _threeCamera = val;
};

export const getThreeRenderer = (): THREE.WebGLRenderer | null => _threeRenderer;
export const setThreeRenderer = (val: THREE.WebGLRenderer | null): void => {
    _threeRenderer = val;
};

export const getThreeBgMesh = (): THREE.Mesh | null => _threeBgMesh;
export const setThreeBgMesh = (val: THREE.Mesh | null): void => {
    _threeBgMesh = val;
};

export const getThreeBgTexture = (): THREE.Texture | null => _threeBgTexture;
export const setThreeBgTexture = (val: THREE.Texture | null): void => {
    _threeBgTexture = val;
};

export const getLastTopHex = (): string => _lastTopHex;
export const setLastTopHex = (val: string): void => {
    _lastTopHex = val;
};

export const getLastBottomHex = (): string => _lastBottomHex;
export const setLastBottomHex = (val: string): void => {
    _lastBottomHex = val;
};

export const getLastBgName = (): string => _lastBgName;
export const setLastBgName = (val: string): void => {
    _lastBgName = val;
};

export const getLastBgProgress = (): number => _lastBgProgress;
export const setLastBgProgress = (val: number): void => {
    _lastBgProgress = val;
};

export const getEarthGroup = (): THREE.Group | null => _earthGroup;
export const setEarthGroup = (val: THREE.Group | null): void => {
    _earthGroup = val;
};

export const getEarthMesh = (): THREE.Mesh | null => _earthMesh;
export const setEarthMesh = (val: THREE.Mesh | null): void => {
    _earthMesh = val;
};

export const getCloudMesh = (): THREE.Mesh | null => _cloudMesh;
export const setCloudMesh = (val: THREE.Mesh | null): void => {
    _cloudMesh = val;
};

export const getAtmosphereMesh = (): THREE.Mesh | null => _atmosphereMesh;
export const setAtmosphereMesh = (val: THREE.Mesh | null): void => {
    _atmosphereMesh = val;
};

export const getThreeNebulaGroup = (): THREE.Group | null => _threeNebulaGroup;
export const setThreeNebulaGroup = (val: THREE.Group | null): void => {
    _threeNebulaGroup = val;
};

export const getThreeNebulaMeshes = (): THREE.Mesh[] => _threeNebulaMeshes;
export const setThreeNebulaMeshes = (val: THREE.Mesh[]): void => {
    _threeNebulaMeshes = val;
};

export const getThreeInitialized = (): boolean => _threeInitialized;
export const setThreeInitialized = (val: boolean): void => {
    _threeInitialized = val;
};

export const getDeepSpacePatterns = (): { x: number; y: number; size: number; rotation: number; opacity: number; color: THREE.Color }[] => _deepSpacePatterns;
export const setDeepSpacePatterns = (val: { x: number; y: number; size: number; rotation: number; opacity: number; color: THREE.Color }[]): void => {
    _deepSpacePatterns = val;
};

// --- Moon state ---
export const getMoonGroup = (): THREE.Group | null => _moonGroup;
export const setMoonGroup = (val: THREE.Group | null): void => {
    _moonGroup = val;
};

export const getMoonMesh = (): THREE.Mesh | null => _moonMesh;
export const setMoonMesh = (val: THREE.Mesh | null): void => {
    _moonMesh = val;
};

export const getMoonY = (): number => _moonY;
export const setMoonY = (val: number): void => {
    _moonY = val;
};

// --- Space Station state ---
export const getSpaceStationGroup = (): THREE.Group | null => _spaceStationGroup;
export const setSpaceStationGroup = (val: THREE.Group | null): void => {
    _spaceStationGroup = val;
};

export const getSpaceStationY = (): number => _spaceStationY;
export const setSpaceStationY = (val: number): void => {
    _spaceStationY = val;
};

export const getStationLightTimer = (): number => _stationLightTimer;
export const setStationLightTimer = (val: number): void => {
    _stationLightTimer = val;
};

// --- Mars state ---
export const getMarsGroup = (): THREE.Group | null => _marsGroup;
export const setMarsGroup = (val: THREE.Group | null): void => {
    _marsGroup = val;
};

export const getMarsMesh = (): THREE.Mesh | null => _marsMesh;
export const setMarsMesh = (val: THREE.Mesh | null): void => {
    _marsMesh = val;
};

export const getMarsY = (): number => _marsY;
export const setMarsY = (val: number): void => {
    _marsY = val;
};

export const getMarsDustParticles = (): { x: number; y: number; vx: number; vy: number; size: number; alpha: number }[] => _marsDustParticles;
export const setMarsDustParticles = (val: { x: number; y: number; vx: number; vy: number; size: number; alpha: number }[]): void => {
    _marsDustParticles = val;
};

export const getPhobosMesh = (): THREE.Mesh | null => _phobosMesh;
export const setPhobosMesh = (val: THREE.Mesh | null): void => {
    _phobosMesh = val;
};

export const getDeimosMesh = (): THREE.Mesh | null => _deimosMesh;
export const setDeimosMesh = (val: THREE.Mesh | null): void => {
    _deimosMesh = val;
};

// --- Jupiter state ---
export const getJupiterGroup = (): THREE.Group | null => _jupiterGroup;
export const setJupiterGroup = (val: THREE.Group | null): void => {
    _jupiterGroup = val;
};

export const getJupiterMesh = (): THREE.Mesh | null => _jupiterMesh;
export const setJupiterMesh = (val: THREE.Mesh | null): void => {
    _jupiterMesh = val;
};

export const getJupiterY = (): number => _jupiterY;
export const setJupiterY = (val: number): void => {
    _jupiterY = val;
};
